// Splits a generated note into its sections, so one section (Subjective,
// Objective...) can be copied into an EMR that has a separate field for each.
// Used by the section copy buttons in the note view
// (components/ui/visits/SectionedNote.tsx).
//
// The Chrome extension has the same function in
// apps/chrome-extension/src/lib/note-sections.ts. Keep the two identical below
// this comment: apps/chrome-extension/test/note-sections.test.mjs checks that
// the code matches and runs the cases in lib/__tests__/noteSectionsCases.ts on
// both. The rules follow ROSC Desktop's section paste
// (apps/desktop/src-tauri/src/sections.rs), with French headings added.

// Splits a note into sections.
//
// - SOAP headings are preferred: Subjective, Objective, Assessment, Plan and
//   "Assessment and Plan" (also "Assessment & Plan", "A/P", "Impression"), in
//   English or French (Subjectif, Objectif, Évaluation, Évaluation et plan).
//   A heading may be "## Subjective", "**Subjective**", "Subjective:",
//   "**Subjective:** text", "S: text" or "SUBJECTIVE". Single letters and
//   abbreviations ("S", "A/P") only count with a colon, and not before a lone
//   measurement ("P: 72" is a pulse). A sentence that starts with the word
//   ("Plan was discussed...") is not a heading.
// - A note with fewer than two SOAP headings splits on its template's own
//   heading lines: "# Heading", "**Heading**", or a short line (six words or
//   fewer) ending in ":" such as "Chief complaint:". List items never count.
// - Fewer than two headings of either kind: no sections.
// - Text before the first heading (a title, a visit line) is the preamble. It
//   is shown but belongs to no section, so it is never pasted into a field.

export type SoapSectionKind =
  | "subjective"
  | "objective"
  | "assessment"
  | "assessmentAndPlan"
  | "plan";

export interface NoteSection {
  // The heading as written, without Markdown or the colon: "Subjective",
  // "SUBJECTIVE", "S", "Chief complaint".
  heading: string;
  // The SOAP section it names, or null for a template's own heading.
  kind: SoapSectionKind | null;
  // Markdown of the section without its heading line. Text on the heading
  // line after the colon ("S: cough x 3 days") is kept.
  body: string;
  // Markdown of the section from its heading line, as written in the note.
  full: string;
}

export interface SplitNote {
  preamble: string;
  sections: NoteSection[];
}

const SOAP_ALIASES: Array<[SoapSectionKind, string[]]> = [
  ["subjective", ["subjective", "s", "subjectif"]],
  ["objective", ["objective", "o", "objectif"]],
  [
    "assessmentAndPlan",
    [
      "assessment and plan",
      "assessment & plan",
      "assessment/plan",
      "a/p",
      "a&p",
      "évaluation et plan",
      "évaluation & plan",
      "évaluation/plan",
      "evaluation et plan",
      "evaluation & plan",
      "evaluation/plan",
      "é/p",
    ],
  ],
  ["assessment", ["assessment", "a", "impression", "évaluation", "evaluation"]],
  ["plan", ["plan", "p"]],
];

interface Heading {
  heading: string;
  kind: SoapSectionKind | null;
  inline: string;
}

// Removes Markdown around a candidate heading: "## ", "**", "__".
const cleanHeading = (line: string): string =>
  line
    .trim()
    .replace(/^#+/, "")
    .trim()
    .replace(/^[*_]+|[*_]+$/g, "")
    .trim();

// "72", "72 bpm", "36.8 °C", "120/80 mmHg", "98%": a vital sign's value.
const MEASUREMENT =
  /^[~<>≤≥]?\s*\d+(?:[.,/]\d+)*\s*(?:bpm|beats\/min|\/min|breaths\/min|mmhg|%|°\s?[cf]?|[cf]|kg|lbs?|cm)?$/iu;

const soapHeading = (line: string): Heading | null => {
  const cleaned = cleanHeading(line);
  const colon = cleaned.indexOf(":");
  const label = (colon >= 0 ? cleaned.slice(0, colon) : cleaned)
    .replace(/^[*_\s]+|[*_\s]+$/g, "")
    .normalize("NFC");
  const key = label.toLowerCase().replace(/\s+/g, " ");
  for (const [kind, aliases] of SOAP_ALIASES) {
    const alias = aliases.find((a) => a === key);
    if (alias === undefined) continue;
    const inline =
      colon >= 0
        ? cleaned
            .slice(colon + 1)
            .replace(/^[*_\s]+/, "")
            .trim()
        : "";
    if (alias.length <= 3) {
      // Single letters and abbreviations only count with a colon ("S: ..."),
      // and not before a lone measurement: "P: 72" is a pulse, not the Plan.
      if (colon < 0 || MEASUREMENT.test(inline)) return null;
    }
    return { heading: label, kind, inline };
  }
  return null;
};

const LIST_ITEM = /^\s*(?:[-+•]|\*(?!\*)|\d+[.)])\s/u;

const templateHeading = (line: string): Heading | null => {
  const text = line.trim();
  if (text === "" || text.length > 60 || LIST_ITEM.test(line)) return null;
  const heading = (value: string): Heading | null => {
    const name = value.replace(/:$/, "").trim();
    return name ? { heading: name, kind: null, inline: "" } : null;
  };
  if (/^#{1,6}\s/.test(text)) return heading(cleanHeading(text));
  if (text.startsWith("**") && text.endsWith("**") && text.length > 4) {
    return heading(cleanHeading(text));
  }
  if (
    text.endsWith(":") &&
    !text.slice(0, -1).includes(":") &&
    text.split(/\s+/).length <= 6
  ) {
    return heading(text);
  }
  return null;
};

const splitWith = (
  lines: string[],
  detect: (line: string) => Heading | null,
): SplitNote => {
  const sections: NoteSection[] = [];
  const preamble: string[] = [];
  let current: { heading: Heading; line: string; lines: string[] } | null =
    null;
  const finish = (): void => {
    if (!current) return;
    const { heading, line, lines: rest } = current;
    const body = heading.inline ? [heading.inline, ...rest] : rest;
    sections.push({
      heading: heading.heading,
      kind: heading.kind,
      body: body.join("\n").trim(),
      full: [line, ...rest].join("\n").trim(),
    });
  };
  for (const line of lines) {
    const heading = detect(line);
    if (heading) {
      finish();
      current = { heading, line, lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      preamble.push(line);
    }
  }
  finish();
  return { preamble: preamble.join("\n").trim(), sections };
};

export const splitNoteSections = (note: string): SplitNote => {
  const lines = note.replace(/\r\n?/g, "\n").split("\n");
  const soap = splitWith(lines, soapHeading);
  if (soap.sections.length >= 2) return soap;
  const template = splitWith(lines, templateHeading);
  if (template.sections.length >= 2) return template;
  return { preamble: note.trim(), sections: [] };
};
